import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItemIcon, ListItemText, ListItemButton, Collapse, Container, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import UserAvatarMenu from './components/common/UserAvatarMenu';
import LandingPage from './components/LandingPage';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PieChartIcon from '@mui/icons-material/PieChart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HistoryIcon from '@mui/icons-material/History';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import InputIcon from '@mui/icons-material/Input';
import DatasetIcon from '@mui/icons-material/Dataset';
import BalanceIcon from '@mui/icons-material/Balance';
import StorageIcon from '@mui/icons-material/Storage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GradeIcon from '@mui/icons-material/Grade';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HubIcon from '@mui/icons-material/Hub';
import RuleIcon from '@mui/icons-material/Rule';
import VerifiedIcon from '@mui/icons-material/Verified';
import BugReportIcon from '@mui/icons-material/BugReport';
import InsightsIcon from '@mui/icons-material/Insights';
import CategoryIcon from '@mui/icons-material/Category';
import CalculateIcon from '@mui/icons-material/Calculate';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import MenuIcon from '@mui/icons-material/Menu';
import RecyclingIcon from '@mui/icons-material/Recycling';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Dashboard from './components/Dashboard';
import Calculations from './components/Calculations';
import ELedgers from './components/ELedgers';
import EmissionRecording from './components/EmissionRecording';
import Allocation from './components/Allocation';
import Transfers from './components/Transfers';
import Reports from './components/Reports';
import ApprovalWorkflows from './components/ApprovalWorkflows';
import AuditTrail from './components/AuditTrail';
import NonConformances from './components/NonConformances';
import EmbeddedEmissions from './components/EmbeddedEmissions';
import MAVManagement from './components/MAVManagement';
import Conservation from './components/Conservation';
import OFPStatus from './components/OFPStatus';
import SupplyChainFlow from './components/SupplyChainFlow';
import VerificationDashboard from './components/VerificationDashboard';
import PolicyDashboard from './components/PolicyDashboard';
import EmissionFactorSearch from './components/EmissionFactorSearch';
import MAVEnforcementDashboard from './components/MAVEnforcementDashboard';
import AnomalyDashboard from './components/AnomalyDashboard';
import QualityMetadata from './components/QualityMetadata';
import CryptoAuditTrail from './components/CryptoAuditTrail';
import IngestionDashboard from './components/IngestionDashboard';
import AIMappingWizard from './components/AIMappingWizard';
import ValidationDashboardIngestion from './components/ValidationDashboardIngestion';
import SourceSystemManager from './components/SourceSystemManager';
import QualityDashboard from './components/dataQuality/QualityDashboard';
import QualityRules from './components/dataQuality/QualityRules';
import EntityDetails from './components/dataQuality/EntityDetails';
import QualityDetails from './components/dataQuality/QualityDetails';
import RuleImpact from './components/dataQuality/RuleImpact';
import UserList from './components/admin/UserList';
import UserDetail from './components/admin/UserDetail';
import RoleList from './components/admin/RoleList';
import DPPCompilation from './components/DPPCompilation';
import DPPIssuance from './components/DPPIssuance';
import PACTFootprints from './components/PACTFootprints';
import PACTFootprintDetail from './components/PACTFootprintDetail';
import NodeNetworkPage from './components/NodeNetworkPage';
import NodeNetworkGraph from './components/NodeNetworkGraph';
import NodeDetail from './components/NodeDetail';
import AccountingPeriods from './components/AccountingPeriods';
import Circularity from './components/Circularity';
import Water from './components/Water';

const drawerWidth = 240;

// Navigation is organized into grouped collapsible domains. Groups holding the
// active route auto-expand; every group can be toggled independently.
const navGroups = [
  {
    label: 'Overview',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ],
  },
  {
    label: 'Ledgers & Accounting',
    items: [
      { text: 'E-Ledgers', icon: <AccountBalanceIcon />, path: '/eledgers' },
      { text: 'Allocation', icon: <PieChartIcon />, path: '/allocation' },
      { text: 'Transfers', icon: <SwapHorizIcon />, path: '/transfers' },
      { text: 'Conservation', icon: <BalanceIcon />, path: '/conservation' },
      { text: 'Accounting Periods', icon: <CalendarMonthIcon />, path: '/accounting-periods' },
      { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
    ],
  },
  {
    label: 'Carbon & Emissions',
    items: [
      { text: 'Emissions', icon: <CloudUploadIcon />, path: '/emissions' },
      { text: 'Embedded (S3)', icon: <InputIcon />, path: '/embedded-emissions' },
      { text: 'Factor Search', icon: <SearchIcon />, path: '/emission-factor-search' },
      { text: 'MAV Enforce', icon: <TrendingUpIcon />, path: '/mav-enforcement' },
    ],
  },
  {
    label: 'Circularity',
    items: [
      { text: 'Circularity', icon: <RecyclingIcon />, path: '/circularity' },
    ],
  },
  {
    label: 'Water',
    items: [
      { text: 'Water', icon: <WaterDropIcon />, path: '/water' },
    ],
  },
  {
    label: 'Verification',
    items: [
      { text: 'AI Verification', icon: <SmartToyIcon />, path: '/ai-verification' },
      { text: 'Approvals', icon: <VerifiedUserIcon />, path: '/approvals' },
      { text: 'Audit Trail', icon: <HistoryIcon />, path: '/audit-trail' },
      { text: 'Non-Conformances', icon: <ReportProblemIcon />, path: '/non-conformances' },
    ],
  },
  {
    label: 'AI-Native',
    items: [
      { text: 'Policies', icon: <GavelIcon />, path: '/policies' },
      { text: 'Anomalies', icon: <WarningAmberIcon />, path: '/anomalies' },
      { text: 'Quality Meta', icon: <GradeIcon />, path: '/quality-metadata' },
      { text: 'Crypto Audit', icon: <FingerprintIcon />, path: '/crypto-audit' },
    ],
  },
  {
    label: 'Data',
    items: [
      { text: 'Ingestion', icon: <FileUploadIcon />, path: '/ingestion' },
      { text: 'AI Mapping', icon: <AutoAwesomeIcon />, path: '/ai-mapping' },
      { text: 'Validation', icon: <FactCheckIcon />, path: '/data-validation' },
      { text: 'Source Systems', icon: <HubIcon />, path: '/source-systems' },
      { text: 'Quality Dashboard', icon: <VerifiedIcon />, path: '/data-quality' },
      { text: 'Quality Rules', icon: <RuleIcon />, path: '/data-quality/rules' },
      { text: 'Entity Quality', icon: <CategoryIcon />, path: '/data-quality/entities' },
      { text: 'Quality Details', icon: <InsightsIcon />, path: '/data-quality/details' },
      { text: 'Rule Impact', icon: <BugReportIcon />, path: '/data-quality/impact' },
    ],
  },
  {
    label: 'Products & Supply Chain',
    items: [
      { text: 'DPP Compilation', icon: <PlaylistAddIcon />, path: '/dpp-compilation' },
      { text: 'DPP Issuance', icon: <CardMembershipIcon />, path: '/dpp-issuance' },
      { text: 'PACT Footprints', icon: <Inventory2Icon />, path: '/pact-footprints' },
      { text: 'Node Network', icon: <DeviceHubIcon />, path: '/node-network' },
      { text: 'Supply Chain Flow', icon: <AccountTreeIcon />, path: '/supply-chain-flow' },
    ],
  },
  {
    label: 'Reference Data',
    items: [
      { text: 'Calculations', icon: <CalculateIcon />, path: '/calculations' },
      { text: 'MAV Data', icon: <DatasetIcon />, path: '/mav' },
      { text: 'OFP Status', icon: <StorageIcon />, path: '/ofp' },
    ],
  },
];

const isItemActive = (item, pathname) =>
  pathname === item.path || pathname.startsWith(`${item.path}/`);

function AppLayout() {
  const location = useLocation();
  const theme = useTheme();
  // Below md the permanent drawer would squeeze the content off-screen, so the
  // nav becomes a hamburger-opened overlay (temporary) drawer instead.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  // Auto-expand the group holding the active route so navigation state
  // always reflects where the user is.
  useEffect(() => {
    const activeGroup = navGroups.find((g) => g.items.some((i) => isItemActive(i, location.pathname)));
    if (activeGroup) {
      setOpenGroups((prev) => (prev[activeGroup.label] ? prev : { ...prev, [activeGroup.label]: true }));
    }
  }, [location.pathname]);

  const drawerContent = (
    <Box sx={{ overflow: 'auto' }}>
      <List disablePadding>
        {navGroups.map((group) => (
          <React.Fragment key={group.label}>
            <ListItemButton
              onClick={() => setOpenGroups((prev) => ({ ...prev, [group.label]: !prev[group.label] }))}
              sx={{ py: 0.75 }}
            >
              <ListItemText
                primary={group.label}
                primaryTypographyProps={{
                  variant: 'caption',
                  fontWeight: 700,
                  color: 'text.secondary',
                  sx: { letterSpacing: 0.8 },
                }}
              />
              {openGroups[group.label] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </ListItemButton>
            <Collapse in={Boolean(openGroups[group.label])} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.items.map((item) => {
                  const active = isItemActive(item, location.pathname);
                  return (
                    <ListItemButton
                      key={item.text}
                      component={Link}
                      to={item.path}
                      selected={active}
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        pl: 3,
                        py: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit', minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Open navigation menu"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div">
            Carbon Measures
          </Typography>
          {!isMobile && (
            <Typography variant="subtitle2" sx={{ ml: 2, opacity: 0.8 }}>
              E-ledger Accounting System
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <UserAvatarMenu />
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Toolbar />
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/eledgers" element={<ELedgers />} />
          <Route path="/emissions" element={<EmissionRecording />} />
          <Route path="/embedded-emissions" element={<EmbeddedEmissions />} />
          <Route path="/allocation" element={<Allocation />} />
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/circularity" element={<Circularity />} />
          <Route path="/water" element={<Water />} />
          <Route path="/approvals" element={<ApprovalWorkflows />} />
          <Route path="/ai-verification" element={<VerificationDashboard />} />
          <Route path="/audit-trail" element={<AuditTrail />} />
          <Route path="/non-conformances" element={<NonConformances />} />
          <Route path="/policies" element={<PolicyDashboard />} />
          <Route path="/emission-factor-search" element={<EmissionFactorSearch />} />
          <Route path="/mav-enforcement" element={<MAVEnforcementDashboard />} />
          <Route path="/anomalies" element={<AnomalyDashboard />} />
          <Route path="/quality-metadata" element={<QualityMetadata />} />
          <Route path="/crypto-audit" element={<CryptoAuditTrail />} />
          <Route path="/ingestion" element={<IngestionDashboard />} />
          <Route path="/ai-mapping" element={<AIMappingWizard />} />
          <Route path="/data-validation" element={<ValidationDashboardIngestion />} />
          <Route path="/source-systems" element={<SourceSystemManager />} />
          <Route path="/data-quality" element={<QualityDashboard />} />
          <Route path="/data-quality/rules" element={<QualityRules />} />
          <Route path="/data-quality/entities" element={<EntityDetails />} />
          <Route path="/data-quality/details" element={<QualityDetails />} />
          <Route path="/data-quality/impact" element={<RuleImpact />} />
          <Route path="/mav" element={<MAVManagement />} />
          <Route path="/calculations" element={<Calculations />} />
          <Route path="/conservation" element={<Conservation />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/supply-chain-flow" element={<SupplyChainFlow />} />
          <Route path="/ofp" element={<OFPStatus />} />
          <Route path="/accounting-periods" element={<AccountingPeriods />} />
          <Route path="/dpp-compilation" element={<DPPCompilation />} />
          <Route path="/dpp-issuance" element={<DPPIssuance />} />
          <Route path="/pact-footprints" element={<PACTFootprints />} />
          <Route path="/pact-footprints/:id" element={<PACTFootprintDetail />} />
          <Route path="/node-network" element={<NodeNetworkPage />} />
          <Route path="/node-network/:networkId/graph" element={<NodeNetworkGraph />} />
          <Route path="/node-network/nodes/:nodeId" element={<NodeDetail />} />
          <Route element={<AdminRoute />}>
            <Route path="/admin/users" element={<UserList />} />
            <Route path="/admin/users/:id" element={<UserDetail />} />
            <Route path="/admin/roles" element={<RoleList />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
